/**
 * 시장 데이터 관리 Pinia Store
 * WebSocket 데이터를 중앙에서 관리하고 반응적으로 UI에 전달
 * API 응답 기반으로 종목 목록을 관리
 */
import { ALL_SYMBOL_CODES } from '@template/types';
import { ref, computed, markRaw } from 'vue';
import { defineStore } from 'pinia';
export const useMarketDataStore = defineStore('marketData', () => {
    // === 상태 ===
    const marketData = ref([]);
    const isInitialized = ref(false);
    // API에서 허용된 종목 목록
    const allowedSymbols = ref(new Set());
    // === 배치 업데이트를 위한 버퍼 ===
    const updateBuffer = markRaw(new Map());
    let isScheduled = false;
    // === 계산된 속성 ===
    const getSymbolData = computed(() => (symbol) => {
        // 초기화되지 않았다면 초기화 실행
        if (!isInitialized.value) {
            initializeMarketData();
        }
        return marketData.value.find((item) => item.symbol === symbol);
    });
    const getSymbolCount = computed(() => marketData.value.length);
    /**
     * 실제 사용 가능한 종목 코드 목록 (정렬된 배열)
     * API 응답으로 업데이트된 종목 목록을 반환
     * 초기화되지 않았다면 자동으로 초기화
     */
    const availableSymbolCodes = computed(() => {
        if (!isInitialized.value) {
            initializeMarketData();
        }
        return Array.from(allowedSymbols.value).sort();
    });
    // === 액션 ===
    /**
     * OrderStocks API 스냅샷으로 시장 데이터 초기화
     * 메타데이터와 매수/매도 가격을 기반으로 초기 상태를 구성
     */
    const initializeMarketDataFromSnapshot = (metadataList, buyPrices, sellPrices) => {
        const symbolCodes = metadataList.map((meta) => meta.stockCd);
        // 허용된 종목 목록 업데이트
        allowedSymbols.value = new Set(symbolCodes);
        // 기존 고가/저가 유지
        // initializeMarketDataFromSnapshot이 loadDailyHighLow 이후 호출될 때 race condition 방지
        const existingBySymbol = new Map(marketData.value.map((d) => [d.symbol, { high: d.high, low: d.low }]));
        // 초기 데이터 구성
        const initializedMarketData = metadataList.map((meta) => {
            const symbol = meta.stockCd;
            const existing = existingBySymbol.get(symbol);
            const rawBuyPrice = buyPrices?.[symbol];
            const rawSellPrice = sellPrices?.[symbol];
            const buyPriceSource = Number(rawBuyPrice);
            const sellPriceSource = Number(rawSellPrice);
            const buyPrice = Number.isFinite(buyPriceSource) ? buyPriceSource : 0;
            const sellPrice = Number.isFinite(sellPriceSource) ? sellPriceSource : 0;
            const closePrice = meta.closePrice ?? 0;
            // 등락률 계산은 매도가 기준으로 계산
            const change = sellPrice - closePrice;
            const changePercent = closePrice ? (change / closePrice) * 100 : 0;
            return {
                symbol,
                buyPrice: Math.round(buyPrice * 100000) / 100000,
                sellPrice: Math.round(sellPrice * 100000) / 100000,
                change: Math.round(change * 100000) / 100000,
                changePercent: Math.round(changePercent * 100) / 100,
                closePrice: closePrice,
                high: existing?.high,
                low: existing?.low,
            };
        });
        marketData.value = initializedMarketData;
        isInitialized.value = true;
    };
    /**
     * 시장 데이터 초기화
     * ALL_SYMBOL_CODES로 초기화 후 API 응답으로 업데이트
     */
    const initializeMarketData = async () => {
        if (isInitialized.value) {
            return;
        }
        // ALL_SYMBOL_CODES로 초기화
        // API 호출 전까지는 기본 종목 목록 사용
        const initialSymbolCodes = [...ALL_SYMBOL_CODES];
        marketData.value = initialSymbolCodes.map((symbolCode) => ({
            symbol: symbolCode,
            change: 0,
            changePercent: 0,
            closePrice: 0,
            sellPrice: 0,
            buyPrice: 0,
            high: undefined,
            low: undefined,
        }));
        allowedSymbols.value = new Set(initialSymbolCodes);
        isInitialized.value = true;
    };
    /**
     * 배치 업데이트 스케줄링
     */
    const scheduleBatchUpdate = () => {
        if (isScheduled)
            return;
        isScheduled = true;
        requestAnimationFrame(() => {
            commitBatchUpdates();
            isScheduled = false;
        });
    };
    /**
     * 배치 업데이트 커밋
     */
    const commitBatchUpdates = () => {
        if (updateBuffer.size === 0)
            return;
        // 모든 업데이트를 한 번에 처리
        for (const [symbol, streamData] of updateBuffer) {
            updateSingleSymbolData(marketData.value, symbol, streamData);
        }
        updateBuffer.clear();
    };
    /**
     * 단일 심볼 데이터 업데이트 (내부 함수)
     */
    const updateSingleSymbolData = (marketDataArray, symbol, streamData) => {
        let dataIndex = marketDataArray.findIndex((data) => data.symbol === symbol);
        // 심볼이 없으면 동적으로 생성
        if (dataIndex === -1) {
            // 새로운 심볼 데이터 추가
            const newSymbolData = {
                symbol: symbol,
                change: 0,
                changePercent: 0,
                closePrice: 0,
                sellPrice: 0,
                buyPrice: 0,
                high: undefined,
                low: undefined,
            };
            marketDataArray.push(newSymbolData);
            dataIndex = marketDataArray.length - 1;
        }
        const newPrice = streamData.sellPrice;
        // 기존 데이터 가져오기
        const currentData = marketDataArray[dataIndex];
        if (!currentData) {
            console.error(`[MarketDataStore] ${symbol} 데이터를 찾을 수 없습니다. dataIndex: ${dataIndex}`);
            return;
        }
        // 전일종가는 최초 API 응답 값 유지
        // 하루에 한 번 설정, 실시간 업데이트 시 변경하지 않음
        const closePrice = currentData.closePrice || 0;
        // 새로운 데이터 계산 (전일종가 기준)
        const change = newPrice - closePrice;
        // 전일종가가 0이면 등락률 계산 불가 (나중에 표시 시 '-'로 처리됨)
        const changePercent = closePrice > 0 ? (change / closePrice) * 100 : NaN;
        // 업데이트된 데이터로 교체
        // 고가/저가는 MarketTicker 메시지로만 업데이트되므로 여기서는 유지
        marketDataArray[dataIndex] = {
            ...currentData,
            change: Math.round(change * 100000) / 100000,
            changePercent: isNaN(changePercent) ? NaN : Math.round(changePercent * 100) / 100,
            closePrice: closePrice, // 전일종가는 기존 값 유지
            sellPrice: streamData.sellPrice !== undefined
                ? Math.round(streamData.sellPrice * 100000) / 100000
                : currentData.sellPrice,
            buyPrice: streamData.buyPrice !== undefined
                ? Math.round(streamData.buyPrice * 100000) / 100000
                : currentData.buyPrice,
            // high와 low는 MarketTicker 메시지로만 업데이트되므로 기존 값 유지
            high: currentData.high,
            low: currentData.low,
        };
    };
    /**
     * WebSocket에서 받은 실시간 데이터로 시장 데이터 업데이트 (배치 처리)
     * API 허용 목록에 있는 종목만 처리
     */
    const updateMarketDataFromStream = (symbol, streamData) => {
        // 초기화되지 않았다면 초기화 실행
        if (!isInitialized.value) {
            initializeMarketData();
        }
        // 허용된 종목인지 확인
        if (!allowedSymbols.value.has(symbol)) {
            return;
        }
        // 버퍼에 데이터 추가
        updateBuffer.set(symbol, streamData);
        // 배치 업데이트 스케줄링
        scheduleBatchUpdate();
    };
    /**
     * 특정 심볼의 데이터 가져오기
     */
    const getSymbolDataBySymbol = (symbol) => {
        return marketData.value.find((data) => data.symbol === symbol);
    };
    /**
     * 모든 Symbol의 모든 시세 삭제
     * QuoteCancelType 4일 때 호출
     */
    const clearAllMarketData = () => {
        // 모든 심볼의 시세를 초기값으로 리셋
        marketData.value = marketData.value.map((data) => ({
            symbol: data.symbol,
            change: 0,
            changePercent: 0,
            closePrice: data.closePrice, // 전일종가는 유지
            sellPrice: 0,
            buyPrice: 0,
            high: undefined,
            low: undefined,
        }));
    };
    /**
     * 특정 Symbol의 모든 시세 삭제
     * QuoteCancelType 1일 때 호출
     * @param symbol - 초기화할 종목 코드
     */
    const clearSymbolMarketData = (symbol) => {
        const dataIndex = marketData.value.findIndex((data) => data.symbol === symbol);
        if (dataIndex === -1) {
            // 심볼이 없으면 무시
            return;
        }
        // 해당 심볼의 시세를 초기값으로 리셋
        const currentData = marketData.value[dataIndex];
        marketData.value[dataIndex] = {
            symbol: currentData.symbol,
            change: 0,
            changePercent: 0,
            closePrice: currentData.closePrice, // 전일종가는 유지
            sellPrice: 0,
            buyPrice: 0,
            high: undefined,
            low: undefined,
        };
    };
    /**
     * 특정 종목의 당일 고가/저가를 API에서 로드
     * 초기 로드용으로만 사용하며, WebSocket 연결 후에는 실시간 데이터로 업데이트됩니다.
     *
     * 주의: WebSocket 연결 후에는 MarketTicker 메시지로 자동 업데이트되므로,
     * 호출하는 곳에서 WebSocket 연결 상태를 확인하고 필요시에만 호출하는 것을 권장합니다.
     *
     * @param symbol - 종목 코드
     * @param quoteService - QuoteService 인스턴스 (순환 의존성 방지를 위해 파라미터로 받음)
     * @param accountNo - 계좌 번호 (순환 의존성 방지를 위해 파라미터로 받음)
     * @param forceLoad - WebSocket 연결 여부와 관계없이 강제로 로드할지 여부 (기본값: false)
     */
    const loadDailyHighLow = async (symbol, quoteService, accountNo, forceLoad = false) => {
        if (!symbol)
            return;
        // 중복 호출 방지를 위해 이미 고가/저가가 있으면 스킵
        // forceLoad가 true인 경우에도 스킵
        const symbolData = getSymbolDataBySymbol(symbol);
        if (symbolData?.high !== undefined && symbolData?.low !== undefined) {
            return;
        }
        try {
            const response = await quoteService.getStockDailyOHLC(symbol, {
                ...(accountNo && { accountNo }),
            });
            if (response.data) {
                const { high, low } = response.data;
                if (high !== undefined && low !== undefined) {
                    setDailyHighLow(symbol, high, low);
                }
            }
        }
        catch (error) {
            console.error(`[MarketDataStore] ${symbol} 당일 고가/저가 로드 실패:`, error);
        }
    };
    /**
     * 특정 종목의 당일 고가/저가 설정
     * getStockDailyOHLC API 응답으로부터 초기 고가/저가를 설정할 때 사용
     * 값이 실제로 변경된 경우에만 업데이트하여 불필요한 리렌더링 방지
     * @param symbol - 종목 코드
     * @param high - 고가
     * @param low - 저가
     */
    const setDailyHighLow = (symbol, high, low) => {
        const dataIndex = marketData.value.findIndex((data) => data.symbol === symbol);
        if (dataIndex === -1) {
            // 심볼이 없으면 동적으로 생성
            const newSymbolData = {
                symbol: symbol,
                change: 0,
                changePercent: 0,
                closePrice: 0,
                sellPrice: 0,
                buyPrice: 0,
                high: Math.round(high * 100000) / 100000,
                low: Math.round(low * 100000) / 100000,
            };
            marketData.value.push(newSymbolData);
            return;
        }
        // 기존 데이터 가져오기
        const currentData = marketData.value[dataIndex];
        const roundedHigh = Math.round(high * 100000) / 100000;
        const roundedLow = Math.round(low * 100000) / 100000;
        // 값이 실제로 변경된 경우에만 업데이트 (불필요한 리렌더링 방지)
        const hasHighChanged = currentData.high !== roundedHigh;
        const hasLowChanged = currentData.low !== roundedLow;
        if (!hasHighChanged && !hasLowChanged) {
            // 값이 변경되지 않았으면 업데이트 스킵
            return;
        }
        // 변경된 값만 업데이트
        marketData.value[dataIndex] = {
            ...currentData,
            ...(hasHighChanged && { high: roundedHigh }),
            ...(hasLowChanged && { low: roundedLow }),
        };
    };
    return {
        // 상태
        marketData,
        isInitialized,
        allowedSymbols,
        // 계산된 속성
        getSymbolData,
        getSymbolCount,
        availableSymbolCodes,
        // 액션
        initializeMarketData,
        initializeMarketDataFromSnapshot,
        updateMarketDataFromStream, // 배치 처리 (기본)
        getSymbolDataBySymbol,
        clearAllMarketData,
        clearSymbolMarketData,
        setDailyHighLow,
        loadDailyHighLow,
    };
});
//# sourceMappingURL=useMarketDataStore.js.map